/**
 * Neighborhood partition — P1 of the board-layout visual contract
 * (docs/superpowers/plans/2026-07-07-board-layout-contract.md, R1).
 *
 * Deterministically splits a component's nodes into NEIGHBORHOODS — the
 * jurisdiction units of the two-level layout: proximity rules inside a
 * neighborhood, reading (flow) rules between them, alignment never across
 * a boundary.
 *
 * Rules (R1):
 *  - Operates on RAW topology only — never pin- or port-filtered inputs
 *    (deriving structure post-filter snaps geometry mid-gesture; the
 *    twin-reservation lesson).
 *  - Every SCC is ATOMIC: it lands whole in one neighborhood, oversized
 *    or not. Units below are SCC groups, not nodes.
 *  - Formations (twin shelves → gateway motifs → overlap shelves, the
 *    solver's own detection order) seed neighborhoods; a seed absorbs
 *    every SCC unit it touches.
 *  - Remaining units join the neighborhood holding the PLURALITY of
 *    their raw edge weight (multi-edge count — the interface-level call
 *    count — not deduped topology).
 *  - HYSTERESIS: with a previous assignment, a candidate matching the
 *    unit's previous neighborhood wins unless the best alternative beats
 *    it by more than HYSTERESIS_BAND — an edit must not flip a node's
 *    neighborhood (and macro rank with it) on a near-tie.
 *  - Soft size target: exact-weight ties prefer a neighborhood still
 *    under NEIGHBORHOOD_SIZE_TARGET, then the smaller one.
 */

import { sccComponents } from "./graphDistances.js";
import { detectMotifs, detectShelves, nearTwinReserved } from "./motifs.js";

export interface Neighborhood {
  /** Stable id: `~hood~<min member id>`. */
  readonly id: string;
  /** Sorted member node ids. */
  readonly memberIds: readonly string[];
}

/** Soft size target (calibrated on the P0 fixtures; not a hard cap —
 *  SCCs and formation families exceed it whole). */
export const NEIGHBORHOOD_SIZE_TARGET = 8;

/** A previous-assignment match survives unless a rival is this much
 *  heavier (weight ratio). */
const HYSTERESIS_BAND = 1.2;

/** Memo of recent partitions, keyed by CANONICAL topology (sorted ids +
 *  sorted edge keys + sizes + previous). The partition is a pure
 *  function, so caching is invisible — and load-bearing for gestures: a
 *  drag re-solves every pointermove over UNCHANGED raw topology, and a
 *  cold partition costs ~5ms at 324 nodes against an ~8ms frame. */
const PARTITION_MEMO_MAX = 4;
const partitionMemo = new Map<string, Neighborhood[]>();

export function partitionNeighborhoods(
  ids: readonly string[],
  rawEdges: readonly { from: string; to: string }[],
  sizes?: ReadonlyMap<string, { w: number; h: number }>,
  /** node id → previous neighborhood key (opaque). */
  previous?: ReadonlyMap<string, string>,
): Neighborhood[] {
  const memoKey = [
    [...ids].sort().join(","),
    rawEdges
      .map((e) => `${e.from}>${e.to}`)
      .sort()
      .join(","),
    sizes
      ? [...sizes.entries()]
          .map(([id, s]) => `${id}:${s.w}x${s.h}`)
          .sort()
          .join(",")
      : "",
    previous
      ? [...previous.entries()]
          .map(([id, h]) => `${id}=${h}`)
          .sort()
          .join(",")
      : "",
  ].join(";");
  const cached = partitionMemo.get(memoKey);
  if (cached) return cached;
  const result = computePartition(ids, rawEdges, sizes, previous);
  partitionMemo.set(memoKey, result);
  if (partitionMemo.size > PARTITION_MEMO_MAX) {
    partitionMemo.delete(partitionMemo.keys().next().value!);
  }
  return result;
}

function computePartition(
  ids: readonly string[],
  rawEdges: readonly { from: string; to: string }[],
  sizes?: ReadonlyMap<string, { w: number; h: number }>,
  previous?: ReadonlyMap<string, string>,
): Neighborhood[] {
  const idSet = new Set(ids);
  const valid = rawEdges.filter(
    (e) => e.from !== e.to && idSet.has(e.from) && idSet.has(e.to),
  );
  // Deduped topology for structure detection; raw list keeps weights.
  const dirSeen = new Set<string>();
  const dedup: { from: string; to: string }[] = [];
  for (const e of valid) {
    const k = `${e.from}|${e.to}`;
    if (dirSeen.has(k)) continue;
    dirSeen.add(k);
    dedup.push(e);
  }

  // ---- Units: SCC groups are atomic --------------------------------------
  const compOf = sccComponents(ids, dedup);
  const unitOf = new Map<string, string>(); // node → unit key (min member id)
  {
    const byComp = new Map<number, string[]>();
    for (const id of ids) {
      const c = compOf.get(id);
      if (c === undefined) continue;
      const arr = byComp.get(c) ?? [];
      arr.push(id);
      byComp.set(c, arr);
    }
    for (const members of byComp.values()) {
      const key = [...members].sort()[0]!;
      for (const m of members) unitOf.set(m, key);
    }
  }
  const membersOfUnit = new Map<string, string[]>();
  for (const id of ids) {
    const u = unitOf.get(id)!;
    const arr = membersOfUnit.get(u) ?? [];
    arr.push(id);
    membersOfUnit.set(u, arr);
  }

  // ---- Union-find over units (groups become neighborhoods) ---------------
  const parent = new Map<string, string>();
  const find = (u: string): string => {
    let cur = u;
    while ((parent.get(cur) ?? cur) !== cur) cur = parent.get(cur)!;
    // Path compression.
    let walk = u;
    while ((parent.get(walk) ?? walk) !== cur) {
      const next = parent.get(walk)!;
      parent.set(walk, cur);
      walk = next;
    }
    return cur;
  };
  const union = (a: string, b: string): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra === rb) return;
    // Deterministic root: smaller key wins.
    if (ra < rb) parent.set(rb, ra);
    else parent.set(ra, rb);
  };
  for (const u of membersOfUnit.keys()) parent.set(u, u);

  // ---- Formation seeds (solver's detection order, raw topology) ----------
  const rawTwins = detectShelves(ids, dedup, new Set(), { exactTwins: true });
  // Exact twins plus near-twins (overlapping-but-unequal caller sets): both
  // reserved so no lone sibling crowns a fan-in over shared callers.
  const twinReserved = nearTwinReserved(
    ids,
    dedup,
    new Set(rawTwins.flatMap((s) => s.memberIds)),
  );
  const motifs = detectMotifs(ids, dedup, sizes, twinReserved);
  // Seeds mirror the FLAG-ON solver, which forms NO overlap shelves —
  // twins and gateway motifs are the only formation seeds (the partition
  // exists only in neighborhood mode, so the seeds must match the
  // formations that solve actually builds; see the detectShelves header).
  const seeded = new Set<string>(); // unit keys participating in any seed
  const seedFamilies: string[][] = [
    ...rawTwins.map((s) => [...s.memberIds]),
    ...motifs.map((m) => [m.anchorId, ...m.leafIds]),
  ];
  for (const family of seedFamilies) {
    const units = [...new Set(family.map((id) => unitOf.get(id)!))].sort();
    for (let i = 1; i < units.length; i++) union(units[0]!, units[i]!);
    for (const u of units) seeded.add(u);
  }

  // ---- Unit-pair weights (raw multi-edge counts, undirected) -------------
  const weight = new Map<string, number>(); // "uA|uB" (uA < uB) → count
  for (const e of valid) {
    const ua = unitOf.get(e.from)!;
    const ub = unitOf.get(e.to)!;
    if (ua === ub) continue;
    const key = ua < ub ? `${ua}|${ub}` : `${ub}|${ua}`;
    weight.set(key, (weight.get(key) ?? 0) + 1);
  }
  const unitNeighbors = new Map<string, Set<string>>();
  for (const key of weight.keys()) {
    const [a, b] = key.split("|") as [string, string];
    (unitNeighbors.get(a) ?? unitNeighbors.set(a, new Set()).get(a)!).add(b);
    (unitNeighbors.get(b) ?? unitNeighbors.set(b, new Set()).get(b)!).add(a);
  }
  const pairWeight = (a: string, b: string): number =>
    weight.get(a < b ? `${a}|${b}` : `${b}|${a}`) ?? 0;

  // ---- Plurality assignment of unseeded units -----------------------------
  // Deterministic order: heaviest total weight first (well-connected units
  // anchor early so lighter ones can cascade onto them), then key.
  const unassigned = [...membersOfUnit.keys()]
    .filter((u) => !seeded.has(find(u)))
    .sort((a, b) => {
      let wa = 0;
      let wb = 0;
      for (const n of unitNeighbors.get(a) ?? []) wa += pairWeight(a, n);
      for (const n of unitNeighbors.get(b) ?? []) wb += pairWeight(b, n);
      return wb - wa || (a < b ? -1 : 1);
    });
  const assigned = new Set<string>(); // group roots that exist as targets
  for (const u of membersOfUnit.keys()) {
    if (seeded.has(find(u))) assigned.add(find(u));
  }

  /** Majority previous-hood key over a set of node ids ("" = none). */
  const prevKeyOf = (nodeIds: readonly string[]): string => {
    if (!previous) return "";
    const counts = new Map<string, number>();
    for (const n of nodeIds) {
      const k = previous.get(n);
      if (k === undefined) continue;
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    let best = "";
    let bestN = 0;
    for (const [k, n] of [...counts.entries()].sort()) {
      if (n > bestN) {
        best = k;
        bestN = n;
      }
    }
    return best;
  };

  const groupMembers = (root: string): string[] => {
    const out: string[] = [];
    for (const [u, ms] of membersOfUnit) {
      if (find(u) === root) out.push(...ms);
    }
    return out;
  };

  for (const u of unassigned) {
    if (assigned.has(find(u))) continue; // absorbed by an earlier join
    // Score candidate groups by summed weight from this unit.
    const scores = new Map<string, number>();
    for (const n of unitNeighbors.get(u) ?? []) {
      const root = find(n);
      if (root === find(u)) continue;
      if (!assigned.has(root)) continue; // only join formed groups
      scores.set(root, (scores.get(root) ?? 0) + pairWeight(u, n));
    }
    if (scores.size === 0) {
      assigned.add(find(u)); // starts its own neighborhood
      continue;
    }
    const unitPrev = prevKeyOf(membersOfUnit.get(u)!);
    // R1 spill: a hood AT the size target takes no more plurality
    // joiners — the unit spills to its runner-up, and with every
    // candidate full it anchors its OWN hood, which later units may
    // join. The gate is on the TARGET hood only ("still below target"),
    // NOT on the joint size: a below-target hood absorbs whatever unit
    // is most bound to it, even a unit that lands it past the target.
    // Refusing bound units at the brim was tried and REVERTED on visual
    // evidence — it exiled tightly-coupled satellites and whole gateway
    // wings into singleton districts, and the flow constraints between
    // the now-separate hoods scrambled the client→core→periphery
    // reading of the demo board (metrics improved while the picture got
    // worse; renders decide).
    const candidates = [...scores.entries()]
      .map(([root, w]) => {
        const members = groupMembers(root);
        return {
          root,
          w,
          size: members.length,
          prevMatch: unitPrev !== "" && prevKeyOf(members) === unitPrev,
        };
      })
      .filter((c) => c.size < NEIGHBORHOOD_SIZE_TARGET);
    if (candidates.length === 0) {
      assigned.add(find(u)); // every neighbor hood is full: anchor a new one
      continue;
    }
    candidates.sort(
      (a, b) =>
        b.w - a.w ||
        a.size - b.size ||
        (a.root < b.root ? -1 : 1),
    );
    let chosen = candidates[0]!;
    if (!chosen.prevMatch) {
      const prevCand = candidates.find((c) => c.prevMatch);
      if (prevCand && chosen.w <= prevCand.w * HYSTERESIS_BAND) {
        chosen = prevCand;
      }
    }
    union(u, chosen.root);
    assigned.add(find(u));
  }

  // ---- Emit ---------------------------------------------------------------
  const byRoot = new Map<string, string[]>();
  for (const [u, ms] of membersOfUnit) {
    const root = find(u);
    const arr = byRoot.get(root) ?? [];
    arr.push(...ms);
    byRoot.set(root, arr);
  }
  const hoods: Neighborhood[] = [...byRoot.values()].map((members) => {
    const sorted = [...members].sort();
    return { id: `~hood~${sorted[0]!}`, memberIds: sorted };
  });
  hoods.sort((a, b) => (a.id < b.id ? -1 : 1));
  return hoods;
}
